import React, { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { CgBulb } from 'react-icons/cg';
import NumberFormat from 'react-number-format';
import AgentTip from '../AgentTip';
import { priceRequest } from '../../../../store/slices/cruise';
import formatPriceRequest from '../../../../helpers/formatPriceRequest';
import { AgentTipTypes, TravelTypes } from '../../../../constants';
import calculateAgentTipCommissions from '../../../../utils/calculateAgentTipCommissions';
import withSpinner from '../../../../HOCs/withSpinner';
import AgentCommission from '../../AgentCommission';

const AgentTipListItem = ({
  agentTip,
  agentTipIndex,
  isViewing,
  onToggleTipDetails,
  cruiseDepartDate,
  cruiseReturnDate,
  departureCityId,
  tripId,
  handleViewCabinsClick,
  handleCloseCruiseModal,
  itineraryDepartCity,
}) => {
  const dispatch = useDispatch();
  const { staterooms } = useSelector((state) => state.tripSearch.searchObj);
  const cruisePriceBreakdowns = useSelector((state) => state.cruise.priceBreakdowns);
  const commissions = useMemo(
    () => calculateAgentTipCommissions(cruisePriceBreakdowns, agentTip.itineraries, staterooms),
    [cruisePriceBreakdowns, agentTip.itineraries, staterooms],
  );
  const [loadedCruisePriceBreakdowns, setLoadedCruisePriceBreakdowns] = useState({});

  useEffect(() => {
    if (commissions.commissionCruise === null) {
      agentTip.itineraries.forEach((itinerary) => {
        if (itinerary.mode !== TravelTypes.cruise) {
          return false;
        }
        const { itineraryId } = itinerary.travelOption.itineraryRows[0];
        const { cruiseLineId } = itinerary.travelOption.cruiseLine;
        if (!loadedCruisePriceBreakdowns[itineraryId]) {
          setLoadedCruisePriceBreakdowns((prevState) => ({ ...prevState, [itineraryId]: true }));
          dispatch(
            priceRequest(
              formatPriceRequest({
                itineraryId,
                staterooms,
                cruiseLineId,
              }),
            ),
          );
        }
        return null;
      });
    }
  }, [commissions, staterooms, loadedCruisePriceBreakdowns, agentTip.itineraries, dispatch]);

  const renderCommission = () => (
    <AgentCommission agentCommission={commissions.commissionAgentTip} className="mt-0" />
  );
  return (
    <div>
      <div className={`flex ${!isViewing ? 'mb-3 ' : ''}`}>
        <div
          className={`rounded-md rounded-r-none  p-3 bg-orange-400 text-white ${isViewing ? 'rounded-bl-none' : ''}`}
        >
          <CgBulb size="24" />
        </div>
        <div
          className={` flex flex-wrap flex-row justify-between border border-grey-500 border-opacity-20 rounded-l-none rounded-md w-full inline-block ${isViewing ? 'rounded-br-none' : ''}`}
        >
          <div className="py-3 px-4  inline-block">
            {agentTip.tipDescription}
            {agentTip.tipType !== AgentTipTypes.HotelDealAlert && (
              <b>
                {' '}
                Save up to{' '}
                <NumberFormat
                  value={agentTip.tipSaving}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={0}
                  isNumericString
                />
              </b>
            )}
            {agentTip.tipType === AgentTipTypes.HotelDealAlert && agentTip.dealHotelPrice > 0 && (
              <b>
                {' '}
                Only{' '}
                <NumberFormat
                  value={agentTip.dealHotelPrice}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={0}
                  isNumericString
                />
              </b>
            )}
          </div>
          <div className="flex flex-row items-center">
            <div
              className={`px-4 inline-block  ${commissions.commissionAgentTip === null ? 'flex items-center text-orange-400 pt-1' : ''}`}
            >
              {withSpinner(
                renderCommission,
                commissions.commissionAgentTip === null,
                'Loading commission...',
                'inline',
                'xs',
              )}
            </div>
            {((agentTip.itineraries || []).length > 0 || agentTip.betterFlight) && (
              <a
                tabIndex={agentTipIndex}
                role="button"
                className="float-right text-center py-3 px-4 font-bold text-theme cursor-pointer"
                onClick={onToggleTipDetails}
                onKeyDown={onToggleTipDetails}
              >
                {isViewing ? (
                  <>
                    Hide tip
                    <ExpandLess />{' '}
                  </>
                ) : (
                  <>
                    View tip
                    <ExpandMore />{' '}
                  </>
                )}
              </a>
            )}
          </div>
        </div>
      </div>
      {isViewing && (
        <AgentTip
          agentTip={agentTip}
          tripId={tripId}
          departureCityId={departureCityId}
          cruiseDepartDate={cruiseDepartDate}
          cruiseReturnDate={cruiseReturnDate}
          itineraryDepartCity={itineraryDepartCity}
          handleViewCabinsClick={handleViewCabinsClick}
          handleCloseCruiseModal={handleCloseCruiseModal}
        />
      )}
    </div>
  );
};

AgentTipListItem.propTypes = {
  agentTip: PropTypes.objectOf(PropTypes.objectOf).isRequired,
  agentTipIndex: PropTypes.number.isRequired,
  isViewing: PropTypes.bool.isRequired,
  onToggleTipDetails: PropTypes.func.isRequired,
  cruiseDepartDate: PropTypes.string.isRequired,
  cruiseReturnDate: PropTypes.string.isRequired,
  departureCityId: PropTypes.number.isRequired,
  tripId: PropTypes.string.isRequired,
  handleViewCabinsClick: PropTypes.func.isRequired,
  handleCloseCruiseModal: PropTypes.func.isRequired,
  itineraryDepartCity: PropTypes.string.isRequired,
};

export default memo(AgentTipListItem);
